import { useCallback, useMemo, useState } from "react";

import { FilterFunnel01Icon } from "@/components/Icons/FilterFunnel01Icon";
import { Pagination } from "@/components/Pagination/Pagination";
import { filterProjectsByFilters } from "@/components/ProjectFilter/filterProjectsByFilters";
import type { PortfolioProjectFilterValues } from "@/components/ProjectFilter/PortfolioProjectFilterFields";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { FormField } from "@/design-system/FormField";
import { Inline } from "@/design-system/Inline";
import { NumericBadge } from "@/design-system/NumericBadge";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { useBool } from "@/hooks/useBool";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import type { TranslationKey } from "@/translations";
import { useTranslation } from "@/translations";

import { PortfolioProjectFilterDrawer } from "../../components/PortfolioProjectFilterDrawer";
import type { PortfolioIntegrationConfiguration, PortfolioIntegrationProject } from "./types";

type SetupFilter = "all" | "enabled" | "disabled" | "setupIncomplete" | "notInUse" | "unavailable";
type PortfolioIntegrationRow = PortfolioIntegrationProject<PortfolioIntegrationConfiguration>;

const SETUP_FILTERS = ["all", "enabled", "disabled", "setupIncomplete", "notInUse", "unavailable"] as const;
const PAGE_SIZE = 25;

const SETUP_FILTER_LABELS = {
  all: "page.portfolio-integrations.filter.all",
  enabled: "common.label.enabled",
  disabled: "common.label.disabled",
  setupIncomplete: "page.portfolio-integrations.status.setup-incomplete",
  notInUse: "page.portfolio-integrations.status.not-in-use",
  unavailable: "page.portfolio-integrations.status.unavailable",
} as const satisfies Record<SetupFilter, TranslationKey>;

const columnHelper = createDashboardColumnHelper<PortfolioIntegrationRow>();

type ProjectSelectionTableProps = {
  rows: PortfolioIntegrationProject<PortfolioIntegrationConfiguration>[];
  selectedProjectIds: Set<string>;
  onSelectionChange: (projectIds: Set<string>) => void;
  showIntegrationStatus?: boolean;
};

/** Displays filterable portfolio projects and keeps eligible selections in sync with the parent workflow. */
export function ProjectSelectionTable({
  rows,
  selectedProjectIds,
  onSelectionChange,
  showIntegrationStatus = true,
}: ProjectSelectionTableProps): React.ReactNode {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<PortfolioProjectFilterValues>({});
  const [isFilterOpened, filterOpenHandler] = useBool();
  const [search, setSearch] = useState("");
  const [setupFilter, setSetupFilter] = useState<SetupFilter>("all");
  const [page, setPage] = useState(0);
  const amountActiveFilters = Object.values(filters).filter((value) => value?.length).length;
  const availableSetupFilters = useMemo<readonly SetupFilter[]>(
    () =>
      showIntegrationStatus
        ? SETUP_FILTERS.filter((filter) => filter === "all" || rows.some((row) => row.status === filter))
        : (["all"] as const),
    [rows, showIntegrationStatus],
  );

  const visibleRows = useMemo(() => {
    const visibleProjectIds = new Set(
      filterProjectsByFilters(
        rows.map((row) => row.project),
        { ...filters, search },
      ).map((project) => project.id),
    );

    return rows.filter((row) => {
      if (!visibleProjectIds.has(row.project.id)) {
        return false;
      }

      if (showIntegrationStatus && setupFilter !== "all") {
        return row.status === setupFilter;
      }

      return true;
    });
  }, [filters, rows, search, setupFilter, showIntegrationStatus]);

  const eligibleRows = useMemo(() => visibleRows.filter((row) => row.canManage), [visibleRows]);
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const paginatedRows = visibleRows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const selectedEligibleCount = eligibleRows.filter((row) => selectedProjectIds.has(row.project.id)).length;
  const allEligibleSelected = eligibleRows.length > 0 && selectedEligibleCount === eligibleRows.length;
  const someEligibleSelected = selectedEligibleCount > 0 && !allEligibleSelected;

  const toggleAll = useCallback(() => {
    const next = new Set(selectedProjectIds);
    for (const row of eligibleRows) {
      if (allEligibleSelected) {
        next.delete(row.project.id);
      } else {
        next.add(row.project.id);
      }
    }
    onSelectionChange(next);
  }, [allEligibleSelected, eligibleRows, onSelectionChange, selectedProjectIds]);

  const columns = useMemo(() => {
    const result = [
      columnHelper.display({
        id: "selection",
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        enableSorting: false,
        meta: { cellAlignment: "center" },
        header: () => (
          <Checkbox
            aria-label={t("page.portfolio-integrations.projects.select-all")}
            checked={allEligibleSelected}
            indeterminate={someEligibleSelected}
            disabled={eligibleRows.length === 0}
            onChange={toggleAll}
          />
        ),
        cell: ({ row }) => {
          const integrationProject = row.original;
          const isUnavailable = !integrationProject.canManage;
          const isSelected = selectedProjectIds.has(integrationProject.project.id);

          return (
            <Checkbox
              aria-label={t("page.portfolio-integrations.projects.select-project", {
                project: integrationProject.project.name,
              })}
              checked={isSelected}
              disabled={isUnavailable}
              tooltip={isUnavailable ? t("page.portfolio-integrations.projects.unavailable") : undefined}
              onChange={(event) => {
                const next = new Set(selectedProjectIds);
                if (event.target.checked) {
                  next.add(integrationProject.project.id);
                } else {
                  next.delete(integrationProject.project.id);
                }
                onSelectionChange(next);
              }}
            />
          );
        },
      }),
      columnHelper.accessor((row) => row.project.name, {
        id: "project",
        header: t("page.portfolio.table.header.name"),
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
        enableSorting: false,
        cell: ({ getValue }) => <span className="text-caption-emphasis">{getValue()}</span>,
      }),
      columnHelper.accessor((row) => row.project.owner, {
        id: "owner",
        header: t("page.portfolio.overview.filter.project-owner.label"),
        minSize: TABLE_COLUMN_SIZES.USER.MIN,
        size: TABLE_COLUMN_SIZES.USER.DEFAULT,
        enableSorting: false,
        cell: ({ getValue }) => getValue() || t("common.label.not-applicable"),
      }),
      columnHelper.accessor((row) => row.project.maintenance, {
        id: "maintenance",
        header: t("page.portfolio.overview.filter.project-maintainer.label"),
        minSize: TABLE_COLUMN_SIZES.USER.MIN,
        size: TABLE_COLUMN_SIZES.USER.DEFAULT,
        enableSorting: false,
        cell: ({ getValue }) => getValue() || t("common.label.not-applicable"),
      }),
      columnHelper.accessor((row) => row.project.city, {
        id: "city",
        header: t("page.portfolio.table.header.city"),
        minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
        size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
        enableSorting: false,
        cell: ({ getValue }) => getValue() || t("common.label.not-applicable"),
      }),
    ];

    if (showIntegrationStatus) {
      result.push(
        columnHelper.display({
          id: "setup",
          header: t("page.portfolio-integrations.projects.setup"),
          minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
          size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
          enableSorting: false,
          cell: ({ row }) => {
            if (row.original.status === "unavailable") {
              return <Badge variant="warning">{t("page.portfolio-integrations.status.unavailable")}</Badge>;
            }
            if (row.original.status === "setupIncomplete") {
              return <Badge variant="warning">{t("page.portfolio-integrations.status.setup-incomplete")}</Badge>;
            }
            if (row.original.status === "notInUse") {
              return <Badge>{t("page.portfolio-integrations.status.not-in-use")}</Badge>;
            }

            return <Badge variant="success">{t("page.portfolio-integrations.status.complete")}</Badge>;
          },
        }),
        columnHelper.display({
          id: "activation",
          header: t("page.portfolio-integrations.projects.activation"),
          minSize: TABLE_COLUMN_SIZES.TEXT.MIN,
          size: TABLE_COLUMN_SIZES.TEXT.DEFAULT,
          enableSorting: false,
          cell: ({ row }) => {
            if (row.original.status === "unavailable" || row.original.status === "notInUse") {
              return <Badge>{t("common.label.not-applicable")}</Badge>;
            }

            return (
              <Badge variant={row.original.enabled ? "success" : "default"}>
                {t(row.original.enabled ? "common.label.enabled" : "common.label.disabled")}
              </Badge>
            );
          },
        }),
      );
    }

    return result;
  }, [
    allEligibleSelected,
    eligibleRows.length,
    onSelectionChange,
    selectedProjectIds,
    showIntegrationStatus,
    someEligibleSelected,
    t,
    toggleAll,
  ]);

  const table = useResizableTable(TABLE_KEYS.PORTFOLIO_INTEGRATIONS, {
    data: paginatedRows,
    columns,
  });

  return (
    <>
      <Stack gap="sm">
        <section className="flex w-full grow flex-wrap items-center gap-space-sm @md:flex-nowrap">
          <div className="w-full max-w-search-standard">
            <SearchInput
              aria-label={t("page.portfolio-integrations.projects.search")}
              placeholder={t("page.portfolio-integrations.projects.search")}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
            />
          </div>
          <Button
            data-testid="portfolio-integration-filter-btn"
            icon={<FilterFunnel01Icon />}
            variant="soft"
            onClick={filterOpenHandler.toggle}
            aria-pressed={isFilterOpened}
          >
            <Inline as="span" gap="sm" align="center" wrap={false}>
              {t("common.action.filter")}
              {amountActiveFilters > 0 && <NumericBadge count={amountActiveFilters} variant="filled" />}
            </Inline>
          </Button>
          {showIntegrationStatus && availableSetupFilters.length > 2 && (
            <FormField label={t("page.portfolio-integrations.filter.status")} hideLabel>
              <Select
                width="filter"
                items={availableSetupFilters}
                selected={setupFilter}
                keySelector={(item) => item}
                renderOption={(item) => t(SETUP_FILTER_LABELS[item])}
                onChange={(value) => {
                  setSetupFilter(value);
                  setPage(0);
                }}
              />
            </FormField>
          )}
        </section>

        <Table table={table}>
          <Inline gap="sm" align="center" justify="between">
            <span>
              {t("page.portfolio-integrations.projects.selection-summary", {
                selected: selectedProjectIds.size,
                visible: visibleRows.length,
              })}
            </span>
            {pageCount > 1 && <Pagination count={pageCount} currentIndex={currentPage} onChange={setPage} />}
          </Inline>
        </Table>
      </Stack>

      <PortfolioProjectFilterDrawer
        isOpened={isFilterOpened}
        onOpenChange={filterOpenHandler.set}
        onUpdate={(value) => {
          setFilters(value);
          setPage(0);
        }}
        projects={rows.map((row) => row.project)}
        selectedFilter={filters}
      />
    </>
  );
}
